import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, idem, unwrap } from "../client";
import { qk } from "../keys";
import type { GraphLayoutUpdateBody, ProjectCreate } from "./types";

export function useProjects(workspace: string) {
  return useQuery({
    queryKey: qk.projects(workspace),
    queryFn: () =>
      unwrap(api.GET("/v1/workspaces/{workspace}/projects", { params: { path: { workspace }, query: { limit: 100 } } })),
    enabled: !!workspace,
  });
}

export function useProject(project: string) {
  return useQuery({
    queryKey: qk.project(project),
    queryFn: () => unwrap(api.GET("/v1/projects/{project}", { params: { path: { project } } })),
    enabled: !!project,
  });
}

/** Everything about a project in one call: health per environment, recent deploys, spend, grade. */
export function useProjectStatus(project: string, opts: { refetchInterval?: number } = {}) {
  return useQuery({
    queryKey: qk.projectStatus(project),
    queryFn: () => unwrap(api.GET("/v1/projects/{project}/status", { params: { path: { project } } })),
    refetchInterval: opts.refetchInterval ?? 30_000,
    enabled: !!project,
  });
}

export function useProjectGraph(project: string, environment: string) {
  return useQuery({
    queryKey: qk.graph(project, environment),
    queryFn: () =>
      unwrap(
        api.GET("/v1/projects/{project}/environments/{environment}/graph", {
          params: { path: { project, environment } },
        }),
      ),
    enabled: !!project && !!environment,
  });
}

export function useUpdateGraphLayout(project: string, environment: string) {
  return useMutation({
    mutationFn: (body: GraphLayoutUpdateBody) =>
      unwrap(
        api.PATCH("/v1/projects/{project}/environments/{environment}/graph/layout", {
          params: { path: { project, environment }, header: idem() },
          body,
        }),
      ),
  });
}

export function useCreateProject(workspace: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ProjectCreate) =>
      unwrap(api.POST("/v1/workspaces/{workspace}/projects", { params: { path: { workspace }, header: idem() }, body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.projects(workspace) }),
  });
}

export function useEnvironments(project: string) {
  return useQuery({
    queryKey: qk.environments(project),
    queryFn: () => unwrap(api.GET("/v1/projects/{project}/environments", { params: { path: { project } } })),
    staleTime: 60_000,
    enabled: !!project,
  });
}

export function useServices(project: string) {
  return useQuery({
    queryKey: qk.services(project),
    queryFn: () => unwrap(api.GET("/v1/projects/{project}/services", { params: { path: { project } } })),
    enabled: !!project,
  });
}

export function useServiceInstances(project: string, environment: string) {
  return useQuery({
    queryKey: qk.serviceInstances(project, environment),
    queryFn: () =>
      unwrap(
        api.GET("/v1/projects/{project}/environments/{environment}/services", {
          params: { path: { project, environment } },
        }),
      ),
    refetchInterval: 15_000,
    enabled: !!project && !!environment,
  });
}

export function useServiceInstance(project: string, environment: string, service: string) {
  return useQuery({
    queryKey: qk.serviceInstance(project, environment, service),
    queryFn: () =>
      unwrap(
        api.GET("/v1/projects/{project}/environments/{environment}/services/{service}", {
          params: { path: { project, environment, service } },
        }),
      ),
    enabled: !!project && !!environment && !!service,
  });
}

export function useVolumes(project: string, environment: string) {
  return useQuery({
    queryKey: qk.volumes(project, environment),
    queryFn: () =>
      unwrap(
        api.GET("/v1/projects/{project}/environments/{environment}/volumes", {
          params: { path: { project, environment } },
        }),
      ),
    enabled: !!project && !!environment,
  });
}

export function useManifest(project: string) {
  return useQuery({
    queryKey: qk.manifest(project),
    queryFn: () => unwrap(api.GET("/v1/projects/{project}/manifest", { params: { path: { project } } })),
    enabled: !!project,
  });
}

export function useManifestStatus(project: string) {
  return useQuery({
    queryKey: qk.manifestStatus(project),
    queryFn: () => unwrap(api.GET("/v1/projects/{project}/manifest/status", { params: { path: { project } } })),
    enabled: !!project,
  });
}

export function useProjectOperations(project: string) {
  return useQuery({
    queryKey: qk.operations(project),
    queryFn: () => unwrap(api.GET("/v1/projects/{project}/operations", { params: { path: { project } } })),
    enabled: !!project,
  });
}
